import GNEAsyncPagination from 'components/shared/dropdown-with-pagination/GNEAsyncPagination';
import OverlayTrigger from 'components/shared/tooltip/Tooltip';
import {
  ALL,
  CUSTOMER_TYPE_ENTITY,
  CUSTOMER_TYPE_FLAG,
  PolicyDetailsField,
} from 'constants/constants';
import { useRef, useState } from 'react';
import { Col, Row, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { wrapMenuList } from 'react-select-async-paginate';
import { loadDropdownOption } from 'store/customer/customer-view-actions';
import { useQueryClient } from '@tanstack/react-query';

import {
  buildDropdownExpression,
  dropdownOptions,
  formatOptionLabel,
  getFormatedOptionLabel,
  isValidSearchText,
  ValueContainer,
} from 'utils/utils';

const PolicyDetailsFilters = ({
  handleFilterChange,
  selectedPayer,
  selectedPlan,
  selectedProduct,
  selectedIndication,
  selectedCompetitorProduct,
  selectedEcosystemName,
  selectedBookOfBusiness,
  selectedBenefitType,
  selectedPolicyState,
}) => {
  const firstLoading = useRef(true);
  const shouldLoadMore = useRef(false);
  const selectedCustomer = useSelector(
    (state) => state?.CustomerView?.selectedCustomer
  );
  const [offset, setOffset] = useState(0);

  const selectedView = useSelector(
    (state) => state?.CustomerView?.selectedView
  );

  const handleOnChange = (value, field) => {
    handleFilterChange(value, field);
  };

  const shouldLoadMoreOptions = (scrollHeight, clientHeight, scrollTop) => {
    if (firstLoading.current) {
      firstLoading.current = false;
      return true;
    }
    const bottomBorder = (scrollHeight - clientHeight) / 2;
    const loadMore = bottomBorder < scrollTop;
    shouldLoadMore.current = loadMore;
    return loadMore;
  };

  const queryClient = useQueryClient();

  const loadOptions = async (inputValue, fieldName, dependency) => {
    if (fieldName === PolicyDetailsField.PolicyState) {
      if (inputValue && inputValue.length < 2) {
        return { options: [] };
      }
    } else {
      if (!isValidSearchText(inputValue)) {
        return {
          options: [],
        };
      }
    }

    // Build Expresssion
    const expression = buildDropdownExpression(dependency);
    const params = {
      offset: firstLoading.current ? 0 : !shouldLoadMore.current ? 0 : offset,
      mdmId: selectedCustomer?.value,
      entityType: selectedCustomer.flag,
      fieldName,
      searchText: inputValue === '' ? undefined : inputValue,
      expression: expression === '' ? undefined : expression,
    };

    // Compose a unique query key for caching
    const queryKey = ['PolicyDetailsFilter-' + fieldName, params];

    // Try to get cached data first
    let data = queryClient.getQueryData(queryKey);

    if (!data) {
      // Fetch and cache
      data = await loadDropdownOption(params);
      queryClient.setQueryData(queryKey, data);
    }

    const { values, currentListSize, hasMoreElement } = data || {
      values: [],
      currentListSize: 0,
      hasMoreElement: false,
    };

    if (hasMoreElement) {
      setOffset((prevValue) => prevValue + currentListSize);
    }
    return {
      // Extract address, label and value from label
      options: getFormatedOptionLabel(values),
      hasMore: hasMoreElement,
    };
  };
  return (
    <div>
      <Row>
        <Col md={2}>
          <label className='title__x-small'>
            Product
            <OverlayTrigger
              overlay={
                <Tooltip className='gne-tooltip'>
                  For Xolair products, formulary data will be specific to the
                  Xolair formulation selected. Sales data will be aggregated at
                  the Xolair brand level.
                </Tooltip>
              }>
              <i className='icon icon__info__gray ms-2'></i>
            </OverlayTrigger>
          </label>
          <GNEAsyncPagination
            value={selectedProduct}
            onChange={(value) => {
              handleOnChange(value, PolicyDetailsField?.Product);
            }}
            placeholder={ALL}
            isMulti={false}
            closeMenuOnSelect={false}
            isClearable={true}
            loadOptions={(searchValue) => {
              return loadOptions(searchValue, PolicyDetailsField?.Product, {
                [PolicyDetailsField.BenefitType]: selectedBenefitType,
                [PolicyDetailsField.PolicyState]: selectedPolicyState,
                [PolicyDetailsField.Indication]: selectedIndication,
                [PolicyDetailsField.CompetitorProduct]:
                  selectedCompetitorProduct,
                [PolicyDetailsField.Payer]: selectedPayer,
                [PolicyDetailsField.Plan]: selectedPlan,
                [PolicyDetailsField.BookOfBusiness]: selectedBookOfBusiness,
              });
            }}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            formatOptionLabel={(data, { context }) =>
              formatOptionLabel(data, { context })
            }
            cacheUniqs={[
              selectedIndication,
              selectedCompetitorProduct,
              selectedPayer,
              selectedPlan,
              selectedBookOfBusiness,
              selectedBenefitType,
              selectedPolicyState,
              selectedCustomer,
            ]}
          />
        </Col>
        <Col md={2}>
          <label className='title__x-small'>
            Indication
            <OverlayTrigger
              overlay={
                <Tooltip className='gne-tooltip'>
                  Select a single Indication to view Policy related data
                </Tooltip>
              }>
              <i className='icon icon__info__gray ms-2'></i>
            </OverlayTrigger>
          </label>
          <GNEAsyncPagination
            value={selectedIndication}
            onChange={(value) => {
              handleOnChange(value, PolicyDetailsField.Indication);
            }}
            placeholder={'Select Indication'}
            isMulti={false}
            closeMenuOnSelect={false}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(searchValue, PolicyDetailsField.Indication, {
                [PolicyDetailsField.Product]: selectedProduct,
                [PolicyDetailsField.CompetitorProduct]:
                  selectedCompetitorProduct,
                [PolicyDetailsField.Payer]: selectedPayer,
                [PolicyDetailsField.Plan]: selectedPlan,
                [PolicyDetailsField.BookOfBusiness]: selectedBookOfBusiness,
                [PolicyDetailsField.BenefitType]: selectedBenefitType,
                [PolicyDetailsField.PolicyState]: selectedPolicyState,
              });
            }}
            isDisabled={!selectedProduct?.value}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            formatOptionLabel={(data, { context }) =>
              formatOptionLabel(data, { context })
            }
            cacheUniqs={[
              selectedProduct,
              selectedCompetitorProduct,
              selectedPayer,
              selectedPlan,
              selectedBookOfBusiness,
              selectedBenefitType,
              selectedPolicyState,
              selectedCustomer,
            ]}
          />
        </Col>
        <Col md={2}>
          <label className='title__x-small'>
            Competitor Brand
            <OverlayTrigger
              overlay={
                <Tooltip className='gne-tooltip'>
                  Select a single Competitor Brand to view Relative Access
                  Position
                </Tooltip>
              }>
              <i className='icon icon__info__gray ms-2'></i>
            </OverlayTrigger>
          </label>
          <GNEAsyncPagination
            value={selectedCompetitorProduct}
            onChange={(value) => {
              handleOnChange(value, PolicyDetailsField.CompetitorProduct);
            }}
            placeholder={'Select Competitor Brand'}
            isMulti={false}
            closeMenuOnSelect={false}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(
                searchValue,
                PolicyDetailsField.CompetitorProduct,
                {
                  [PolicyDetailsField.Indication]: selectedIndication,
                  [PolicyDetailsField.Product]: selectedProduct,
                  [PolicyDetailsField.Payer]: selectedPayer,
                  [PolicyDetailsField.Plan]: selectedPlan,
                  [PolicyDetailsField.BookOfBusiness]: selectedBookOfBusiness,
                  [PolicyDetailsField.BenefitType]: selectedBenefitType,
                  [PolicyDetailsField.PolicyState]: selectedPolicyState,
                }
              );
            }}
            isDisabled={!selectedProduct?.value || !selectedIndication?.value}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            formatOptionLabel={(data, { context }) =>
              formatOptionLabel(data, { context })
            }
            cacheUniqs={[
              selectedProduct,
              selectedIndication,
              selectedCompetitorProduct,
              selectedPayer,
              selectedPlan,
              selectedBookOfBusiness,
              selectedBenefitType,
              selectedPolicyState,
              selectedCustomer,
            ]}
          />
        </Col>

        {selectedCustomer?.flag ===
          CUSTOMER_TYPE_FLAG.get(CUSTOMER_TYPE_ENTITY.PARENTPAYER)?.[0] && (
          <Col md={2}>
            <label className='title__x-small'>Payer</label>
            <GNEAsyncPagination
              value={selectedPayer}
              onChange={(value) => {
                handleOnChange(value, PolicyDetailsField.Payer);
              }}
              placeholder={ALL}
              isMulti={true}
              closeMenuOnSelect={false}
              isClearable
              loadOptions={(searchValue) => {
                return loadOptions(searchValue, PolicyDetailsField.Payer, {
                  [PolicyDetailsField.Product]: selectedProduct,
                  [PolicyDetailsField.Indication]: selectedIndication,
                  [PolicyDetailsField.CompetitorProduct]:
                    selectedCompetitorProduct,
                  [PolicyDetailsField.BookOfBusiness]: selectedBookOfBusiness,
                  [PolicyDetailsField.BenefitType]: selectedBenefitType,
                  [PolicyDetailsField.PolicyState]: selectedPolicyState,
                });
              }}
              shouldLoadMore={shouldLoadMoreOptions}
              components={{
                ValueContainer: ValueContainer,
                Option: wrapMenuList(dropdownOptions),
              }}
              formatOptionLabel={(data, { context }) =>
                formatOptionLabel(data, { context })
              }
              cacheUniqs={[
                selectedProduct,
                selectedIndication,
                selectedCompetitorProduct,
                selectedBookOfBusiness,
                selectedBenefitType,
                selectedPolicyState,
                selectedCustomer,
              ]}
            />
          </Col>
        )}
        {selectedCustomer?.flag ===
          CUSTOMER_TYPE_FLAG.get(CUSTOMER_TYPE_ENTITY.PAYER)?.[0] && (
          <Col md={2}>
            <label className='title__x-small'>Plan</label>
            <GNEAsyncPagination
              value={selectedPlan}
              onChange={(value) => {
                handleOnChange(value, PolicyDetailsField.Plan);
              }}
              placeholder={ALL}
              isMulti={true}
              closeMenuOnSelect={false}
              isClearable
              loadOptions={(searchValue) => {
                return loadOptions(searchValue, PolicyDetailsField.Plan, {
                  [PolicyDetailsField.Product]: selectedProduct,
                  [PolicyDetailsField.Indication]: selectedIndication,
                  [PolicyDetailsField.CompetitorProduct]:
                    selectedCompetitorProduct,
                  [PolicyDetailsField.BookOfBusiness]: selectedBookOfBusiness,
                  [PolicyDetailsField.BenefitType]: selectedBenefitType,
                  [PolicyDetailsField.PolicyState]: selectedPolicyState,
                });
              }}
              shouldLoadMore={shouldLoadMoreOptions}
              components={{
                ValueContainer: ValueContainer,
                Option: wrapMenuList(dropdownOptions),
              }}
              formatOptionLabel={(data, { context }) =>
                formatOptionLabel(data, { context })
              }
              cacheUniqs={[
                selectedProduct,
                selectedIndication,
                selectedCompetitorProduct,
                selectedBookOfBusiness,
                selectedBenefitType,
                selectedPolicyState,
                selectedCustomer,
              ]}
            />
          </Col>
        )}
        <Col md={2}>
          <label className='title__x-small'>Book of Business</label>
          <GNEAsyncPagination
            value={selectedBookOfBusiness}
            onChange={(value) => {
              handleOnChange(value, PolicyDetailsField.BookOfBusiness);
            }}
            placeholder={ALL}
            isMulti={true}
            closeMenuOnSelect={false}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(
                searchValue,
                PolicyDetailsField.BookOfBusiness,
                {
                  [PolicyDetailsField.Product]: selectedProduct,
                  [PolicyDetailsField.Indication]: selectedIndication,
                  [PolicyDetailsField.CompetitorProduct]:
                    selectedCompetitorProduct,
                  [PolicyDetailsField.Payer]: selectedPayer,
                  [PolicyDetailsField.Plan]: selectedPlan,
                  [PolicyDetailsField.BenefitType]: selectedBenefitType,
                  [PolicyDetailsField.PolicyState]: selectedPolicyState,
                }
              );
            }}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            formatOptionLabel={(data, { context }) =>
              formatOptionLabel(data, { context })
            }
            cacheUniqs={[
              selectedProduct,
              selectedPlan,
              selectedIndication,
              selectedCompetitorProduct,
              selectedPayer,
              selectedBenefitType,
              selectedPolicyState,
              selectedCustomer,
            ]}
          />
        </Col>
        <Col md={2}>
          <label className='title__x-small'>Benefit Type</label>
          <GNEAsyncPagination
            value={selectedBenefitType}
            onChange={(value) => {
              handleOnChange(value, PolicyDetailsField.BenefitType);
            }}
            placeholder={ALL}
            isMulti={true}
            closeMenuOnSelect={false}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(searchValue, PolicyDetailsField.BenefitType, {
                [PolicyDetailsField.Product]: selectedProduct,
                [PolicyDetailsField.Indication]: selectedIndication,
                [PolicyDetailsField.CompetitorProduct]:
                  selectedCompetitorProduct,
                [PolicyDetailsField.Payer]: selectedPayer,
                [PolicyDetailsField.Plan]: selectedPlan,
                [PolicyDetailsField.BookOfBusiness]: selectedBookOfBusiness,
                [PolicyDetailsField.PolicyState]: selectedPolicyState,
              });
            }}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            formatOptionLabel={(data, { context }) =>
              formatOptionLabel(data, { context })
            }
            cacheUniqs={[
              selectedProduct,
              selectedIndication,
              selectedCompetitorProduct,
              selectedPayer,
              selectedPlan,
              selectedBookOfBusiness,
              selectedPolicyState,
              selectedCustomer,
            ]}
          />
        </Col>
      </Row>
      <Row className='mt-2'>
        {selectedCustomer?.flag ===
          CUSTOMER_TYPE_FLAG.get(CUSTOMER_TYPE_ENTITY.PARENTPAYER)?.[0] && (
          <Col md={2}>
            <label className='title__x-small'>
              Ecosystem
              <OverlayTrigger
                overlay={
                  <Tooltip className='gne-tooltip'>
                    {`Selecting an Ecosystem will limit the ${selectedView === CUSTOMER_TYPE_ENTITY.PAYER ? 'Plans' : 'Payers'} viewed to those
                  operating in the Ecosystem. This filter will affect Sales metrics.`}
                  </Tooltip>
                }>
                <i className='icon icon__info__gray ms-2'></i>
              </OverlayTrigger>
            </label>
            <GNEAsyncPagination
              value={selectedEcosystemName}
              onChange={(value) => {
                handleOnChange(value, PolicyDetailsField.EcosystemName);
              }}
              placeholder={ALL}
              isMulti={true}
              closeMenuOnSelect={false}
              isClearable
              loadOptions={(searchValue) => {
                return loadOptions(
                  searchValue,
                  PolicyDetailsField.EcosystemName,
                  {}
                );
              }}
              shouldLoadMore={shouldLoadMoreOptions}
              components={{
                ValueContainer: ValueContainer,
                Option: wrapMenuList(dropdownOptions),
              }}
              formatOptionLabel={(data, { context }) =>
                formatOptionLabel(data, { context })
              }
              cacheUniqs={[selectedCustomer]}
            />
          </Col>
        )}
        {selectedCustomer?.flag ===
          CUSTOMER_TYPE_FLAG.get(CUSTOMER_TYPE_ENTITY.PAYER)?.[0] && (
          <Col md={2}>
            <label className='title__x-small'>
              Policy State
              <OverlayTrigger
                overlay={
                  <Tooltip className='gne-tooltip'>
                    Policy State is a required filter to populate Policy
                    Details. Selecting the Policy State will show the policy
                    details specific to the Plan and State.
                  </Tooltip>
                }>
                <i className='icon icon__info__gray ms-2'></i>
              </OverlayTrigger>
            </label>
            <GNEAsyncPagination
              value={selectedPolicyState}
              onChange={(value) => {
                handleOnChange(value, PolicyDetailsField.PolicyState);
              }}
              placeholder={ALL}
              isMulti={false}
              closeMenuOnSelect={false}
              isClearable
              loadOptions={(searchValue) => {
                return loadOptions(
                  searchValue,
                  PolicyDetailsField.PolicyState,
                  {
                    [PolicyDetailsField.Product]: selectedProduct,
                    [PolicyDetailsField.Indication]: selectedIndication,
                    [PolicyDetailsField.CompetitorProduct]:
                      selectedCompetitorProduct,
                    [PolicyDetailsField.Payer]: selectedPayer,
                    [PolicyDetailsField.Plan]: selectedPlan,
                    [PolicyDetailsField.BookOfBusiness]: selectedBookOfBusiness,
                    [PolicyDetailsField.BenefitType]: selectedBenefitType,
                  }
                );
              }}
              shouldLoadMore={shouldLoadMoreOptions}
              components={{
                ValueContainer: ValueContainer,
                Option: wrapMenuList(dropdownOptions),
              }}
              formatOptionLabel={(data, { context }) =>
                formatOptionLabel(data, { context })
              }
              cacheUniqs={[
                selectedProduct,
                selectedPlan,
                selectedIndication,
                selectedCompetitorProduct,
                selectedPayer,
                selectedBookOfBusiness,
                selectedBenefitType,
                selectedPolicyState,
                selectedCustomer,
              ]}
            />
          </Col>
        )}
      </Row>
    </div>
  );
};

export default PolicyDetailsFilters;
